import { Button, Group, List, Modal, Stack, Text } from "@mantine/core";
import { type JSX, useEffect } from "react";

import { useAnalytics } from "@/analytics";
import { env } from "@/config";
import { useLogger } from "@/sdk/logging/LoggerProvider";

import { useTroubleshootingStore } from "./troubleshoot.store";

/**
 * Shown in the voice build when a contact is missed. The trigger carries no cause, so the steps
 * name no tool — they hold whether the audio came from EVL or from this app's own softphone.
 */
const MISSED_CALL_GUIDANCE = {
    title: "Call failed to connect",
    steps: [
        "Check your headset is plugged in and powered on",
        "Go to Available to start receiving calls again",
        "If it happens again, contact the Service Desk",
    ],
};

/** Routing-profile guidance shown in the chat build (a voice contact reached a chat expert). */
const CHANNEL_MISMATCH_GUIDANCE = {
    title: "Missed a voice contact while in chat mode",
    steps: [
        "Your routing profile may still be set to voice",
        "Ask your supervisor to switch you to the chat profile",
        "Then set yourself to Available",
    ],
};

export function Troubleshooting(): JSX.Element | null {
    const logger = useLogger({ module: "Troubleshooting" });
    const { setIsTroubleshooting } = useTroubleshootingStore();
    const { dispatcher } = useAnalytics();

    // In the chat build a "technical issues" event can only come from a mis-routed voice
    // contact, i.e. the routing profile is still voice-only - not an audio failure.
    const isChatBuild = env.channel === "chat";
    const guidance = isChatBuild ? CHANNEL_MISMATCH_GUIDANCE : MISSED_CALL_GUIDANCE;

    const handleCloseTroubleshooting = () => {
        setIsTroubleshooting(false);
    };

    useEffect(() => {
        logger.info("Troubleshooting | open");
        if (isChatBuild) {
            void dispatcher.dispatchBusinessEvent("ChannelMismatchDetected", { appChannel: env.channel });
        } else {
            void dispatcher.dispatchBusinessEvent("TroubleshootingOpen");
        }
    }, [dispatcher, logger, isChatBuild]);

    const onClose = () => {
        // Keep open/close events paired per channel so analytics funnels don't undercount.
        void dispatcher.dispatchBusinessEvent(isChatBuild ? "ChannelMismatchClose" : "TroubleshootingClose");
        logger.info("Troubleshooting | close");
        setIsTroubleshooting(false);
    };

    return (
        <Modal
            opened
            centered
            onClose={onClose}
            withCloseButton={false}
            closeOnClickOutside={false}
            closeOnEscape={false}
        >
            <Stack>
                <Text size="lg" c="gray.3">
                    {guidance.title}
                </Text>
                <List mt="sm" size="sm" c="gray.6">
                    {guidance.steps.map((step) => (
                        <List.Item key={step}>{step}</List.Item>
                    ))}
                </List>
                <Group justify="right">
                    <Button onClick={handleCloseTroubleshooting}>Ok</Button>
                </Group>
            </Stack>
        </Modal>
    );
}
